import { useState, useEffect, useCallback } from 'react';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { AModal, ASwitch, AInput } from '../../../components';
import { IFinancialPlan } from '../../../shared/interfaces';
import { Form } from 'antd';
import { useToast } from '../../../hooks/toast';
import {
  schemaFinancialPlanCreate,
  schemaFinancialPlanEdit,
} from '../schemas/yup.financial-plan.schema';
import { patchFinancialPlan, postFinancialPlan } from '../../../http';
import { useI18n } from '../../../shared/i18n';

interface IFinancialPlanProps {
  open: boolean;
  close: () => void;
  financialPlan: IFinancialPlan | null;
  setFinancialPlans: React.Dispatch<React.SetStateAction<IFinancialPlan[]>>;
}

const FinancialPlanCreateEdit: React.FC<IFinancialPlanProps> = ({
  open,
  close,
  financialPlan,
  setFinancialPlans,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const [description, setDescription] = useState<string>('');
  const [status, setStatus] = useState<boolean>(false);

  const handleCreate = useCallback(async () => {
    schemaFinancialPlanCreate
      .validate({
        description,
      })
      .then(async (validated) => {
        await postFinancialPlan(validated)
          .then((data) => {
            setFinancialPlans((state) => [...state, data]);
            addToast({
              type: 'success',
              title: t('financialPlansModal.toast.successTitle'),
              description: t('financialPlansModal.toast.createdDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('financialPlansModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('financialPlansModal.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('financialPlansModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [description, status, t, addToast, close, setFinancialPlans]);

  const handleEdit = useCallback(async () => {
    schemaFinancialPlanEdit
      .validate({
        description,
        status,
      })
      .then(async (validated) => {
        await patchFinancialPlan(financialPlan?.id, validated)
          .then((data) => {
            setFinancialPlans((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: t('financialPlansModal.toast.infoTitle'),
              description: t('financialPlansModal.toast.updatedDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('financialPlansModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('financialPlansModal.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('financialPlansModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [
    description,
    status,
    t,
    addToast,
    close,
    financialPlan,
    setFinancialPlans,
  ]);

  useEffect(() => {
    if (financialPlan) {
      setDescription(financialPlan.description);
      setStatus(financialPlan.status);
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        financialPlan ? (
          <>
            <EditOutlined /> {t('financialPlansModal.titleEdit')}
          </>
        ) : (
          <>
            <FormOutlined /> {t('financialPlansModal.titleCreate')}
          </>
        )
      }
      onOk={financialPlan ? handleEdit : handleCreate}
    >
      <Form
        layout="vertical"
        initialValues={{ layout: financialPlan }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item label={t('financialPlansModal.fields.description')}>
          <AInput
            placeholder={t('financialPlansModal.fields.descriptionPlaceholder')}
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Item>
        {financialPlan && (
          <Form.Item label={t('financialPlansModal.fields.active')}>
            <ASwitch
              checked={status}
              checkedChildren={t('common.yes')}
              unCheckedChildren={t('common.no')}
              onChange={() => {
                setStatus(!status);
              }}
            />
          </Form.Item>
        )}
      </Form>
    </AModal>
  );
};

export default FinancialPlanCreateEdit;
